<template>
    <div class="space-y-3">
        <div class="flex justify-end text-sm text-slate-600 dark:text-slate-400">
            <!-- <span>{{ levelUpdate.xpForCurrentLevel }} XP</span> -->
            <!-- <span class="font-semibold text-slate-800 dark:text-slate-200"> -->
            <span>
                {{ levelUpdate.newXp }} / {{ levelUpdate.xpForNextLevel }} XP
            </span>
            <!-- <span>{{ levelUpdate.xpForNextLevel }} XP</span> -->
        </div>

        <div
            class="relative h-5 bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800 rounded-full overflow-hidden shadow-inner border border-slate-300/50 dark:border-slate-600/50">
            <!-- Old progress with muted gradient -->
            <div ref="oldProgressBar"
                class="absolute h-full bg-radial from-green-700 to-green-800 rounded-l-full transition-all ease-out"
                :style="{
                    width: animatedOldProgress + '%',
                    transitionDuration: oldProgressAnimating ? '0ms' : '300ms'
                }">
            </div>
            <!-- New progress with refined gradient -->
            <div ref="newProgressBar"
                class="absolute h-full bg-gradient-to-r from-emerald-400 to-teal-500 dark:from-green-600 dark:to-green-800 transition-all ease-out"
                :style="{
                    left: oldProgressWidth + '%',
                    width: animatedNewProgress + '%',
                    transitionDuration: newProgressAnimating ? '0ms' : '300ms'
                }">
                <!-- More prominent shine effect -->
                <div class="absolute inset-0 bg-radial-[at_50%_75%] to-transparent from-white/50 animate-pulse">
                </div>
            </div>
            <!-- Refined top highlight -->
            <div class="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-white/40 to-white/20 rounded-t-full">
            </div>
        </div>

        <div class="text-xs text-slate-500 dark:text-slate-400 text-center">
            <Tag :value="$t('gainedNXp', { xp: levelUpdate.newXp! - levelUpdate.oldXp! })" severity="success"
                class="text-xs shadow-sm" />
        </div>
    </div>
</template>

<script setup lang="ts">
import type { LevelUpdateDto } from "@/client";
import Tag from 'primevue/tag';
import { onMounted, ref } from "vue";

const props = defineProps<{
    levelUpdate: LevelUpdateDto;
    showAnimation: boolean;
}>();

const oldProgressWidth = ref(0);
const newProgressWidth = ref(0);
const animatedOldProgress = ref(0);
const animatedNewProgress = ref(0);
const oldProgressAnimating = ref(false);
const newProgressAnimating = ref(false);

// Animation function with easing (decreasing speed)
const easeOutQuart = (t: number): number => {
    return 1 - Math.pow(1 - t, 4);
};

const animateProgress = (
    targetValue: number,
    currentRef: typeof animatedOldProgress,
    animatingFlag: typeof oldProgressAnimating,
    duration: number = 1500
): Promise<void> => {
    return new Promise((resolve) => {
        animatingFlag.value = true;
        const startValue = currentRef.value;
        const startTime = performance.now();

        const animate = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Apply easing function for decreasing speed
            const easedProgress = easeOutQuart(progress);

            currentRef.value = startValue + (targetValue - startValue) * easedProgress;

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                animatingFlag.value = false;
                resolve();
            }
        };

        requestAnimationFrame(animate);
    });
};

onMounted(async () => {
    const oldPercentage = getOldProgressPercentage();
    const newPercentage = getNewProgressPercentage();

    oldProgressWidth.value = oldPercentage;
    newProgressWidth.value = newPercentage - oldPercentage;

    if (props.showAnimation) {
        // First animate the old progress from 0 to its target value
        if (oldPercentage > 0) {
            await animateProgress(oldPercentage, animatedOldProgress, oldProgressAnimating, 1500);
        } else {
            animatedOldProgress.value = oldPercentage;
        }

        // Then animate the new progress from 0 to its target value
        if (newPercentage - oldPercentage > 0) {
            await animateProgress(newPercentage - oldPercentage, animatedNewProgress, newProgressAnimating, 1500);
        } else {
            animatedNewProgress.value = newPercentage - oldPercentage;
        }
    } else {
        // No animation, set values directly
        animatedOldProgress.value = oldPercentage;
        animatedNewProgress.value = newPercentage - oldPercentage;
    }
});

const getOldProgressPercentage = () => {
    const currentXp = props.levelUpdate.oldXp! - props.levelUpdate.xpForCurrentLevel!;
    const totalXpNeeded = props.levelUpdate.xpForNextLevel! - props.levelUpdate.xpForCurrentLevel!;
    return Math.min(100, Math.max(0, (currentXp / totalXpNeeded) * 100));
}

const getNewProgressPercentage = () => {
    const currentXp = props.levelUpdate.newXp! - props.levelUpdate.xpForCurrentLevel!;
    const totalXpNeeded = props.levelUpdate.xpForNextLevel! - props.levelUpdate.xpForCurrentLevel!;
    return Math.min(100, Math.max(0, (currentXp / totalXpNeeded) * 100));
}
</script>